/* =====================================================================
 * @auw/i18n — profile language / country option helpers.
 *
 * Powers the "preferred language" and "country" dropdowns on the
 * settings page and the onboarding wizard. Uses `Intl.DisplayNames`
 * to render each country / language name in the user's currently
 * selected UI locale, so the dropdown labels follow `i18n.changeLanguage`
 * without the host having to pass a locale down the tree.
 *
 * App display locales are intentionally capped at the five we actually
 * ship translations for (see `./resolveAppLocale`). The BCP-47 region
 * map below covers ~55 countries so the settings page can still auto-
 * fill the user's "home country" from their browser locale even if
 * the country does not have a dedicated UI translation.
 * ===================================================================== */

import i18n from './bootstrap';
import { resolveAppLocale, SUPPORTED_LOCALES, type AppLocale } from './resolveAppLocale';

export interface ProfileLocaleOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * 사용자 프로필에서 사용하는 기본 언어 코드. 앱 UI 번역이 제공되는
 * 5개 AppLocale 중 기본값(한국어)을 BCP 47 형식으로 반환한다.
 */
const DEFAULT_PROFILE_LANGUAGE_CODE = 'ko-KR';

/**
 * 앱이 i18n으로 실제 번역을 제공하는 AppLocale을 사용자 선호 언어 저장 형식
 * (BCP 47 대표 코드)으로 매핑한다. UI 드롭다운과 DB 저장 값 모두 이 맵의 value
 * 다섯 개 중 하나로 제한된다.
 */
const APP_LOCALE_TO_LANGUAGE_CODE: Record<AppLocale, string> = {
  en: 'en-US',
  ko: 'ko-KR',
  ja: 'ja-JP',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-TW',
};

const APP_LANGUAGE_CODES: string[] = SUPPORTED_LOCALES.map(
  (locale) => APP_LOCALE_TO_LANGUAGE_CODE[locale],
);

function toAppLanguageCode(code?: string | null): string {
  return APP_LOCALE_TO_LANGUAGE_CODE[resolveAppLocale(code ?? undefined)];
}

const COUNTRY_LANGUAGE_MAP: Record<string, string> = {
  KR: 'ko-KR',
  US: 'en-US',
  GB: 'en-GB',
  JP: 'ja-JP',
  CN: 'zh-CN',
  TW: 'zh-TW',
  HK: 'zh-TW',
  SG: 'en-US',
  AU: 'en-US',
  CA: 'en-US',
  NZ: 'en-GB',
  DE: 'de-DE',
  FR: 'fr-FR',
  ES: 'es-ES',
  MX: 'es-MX',
  IT: 'it-IT',
  PT: 'pt-PT',
  BR: 'pt-BR',
  NL: 'nl-NL',
  SE: 'sv-SE',
  NO: 'no-NO',
  DK: 'da-DK',
  FI: 'fi-FI',
  PL: 'pl-PL',
  CZ: 'cs-CZ',
  SK: 'sk-SK',
  HU: 'hu-HU',
  RO: 'ro-RO',
  BG: 'bg-BG',
  HR: 'hr-HR',
  RS: 'sr-RS',
  SI: 'sl-SI',
  GR: 'el-GR',
  TR: 'tr-TR',
  SA: 'ar-SA',
  AE: 'ar-SA',
  IL: 'he-IL',
  IR: 'fa-IR',
  IN: 'hi-IN',
  BD: 'bn-BD',
  PK: 'ur-PK',
  TH: 'th-TH',
  VN: 'vi-VN',
  ID: 'id-ID',
  MY: 'ms-MY',
  PH: 'fil-PH',
  KE: 'sw-KE',
  RU: 'ru-RU',
  UA: 'uk-UA',
  LT: 'lt-LT',
  LV: 'lv-LV',
  EE: 'et-EE',
  IS: 'is-IS',
  ZA: 'af-ZA',
};

const COUNTRY_CODES = [
  'KR',
  'US',
  'GB',
  'JP',
  'CN',
  'TW',
  'HK',
  'SG',
  'AU',
  'CA',
  'NZ',
  'DE',
  'FR',
  'ES',
  'MX',
  'IT',
  'PT',
  'BR',
  'NL',
  'SE',
  'NO',
  'DK',
  'FI',
  'PL',
  'CZ',
  'SK',
  'HU',
  'RO',
  'BG',
  'HR',
  'RS',
  'SI',
  'GR',
  'TR',
  'SA',
  'AE',
  'IL',
  'IR',
  'IN',
  'BD',
  'PK',
  'TH',
  'VN',
  'ID',
  'MY',
  'PH',
  'KE',
  'RU',
  'UA',
  'LT',
  'LV',
  'EE',
  'IS',
  'ZA',
];

function getCountryLabel(code: string): string {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      const displayLocale = i18n.language ?? 'en';
      const names = new Intl.DisplayNames([displayLocale], { type: 'region' });
      return names.of(code) ?? code;
    }
  } catch {
    // ignore Intl fallback errors
  }
  return code;
}

function getLanguageLabel(code: string): string {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      const displayLocale = i18n.language ?? 'en';
      const names = new Intl.DisplayNames([displayLocale], { type: 'language' });
      return names.of(code) ?? code;
    }
  } catch {
    // ignore Intl fallback errors
  }
  return code;
}

function normalizeProfileLanguageCodeRaw(code?: string | null): string {
  if (typeof code !== 'string') return DEFAULT_PROFILE_LANGUAGE_CODE;
  const trimmed = code.trim();
  if (!trimmed) return DEFAULT_PROFILE_LANGUAGE_CODE;
  return trimmed;
}

function getCountryDescription(code: string): string | undefined {
  const languageCode = COUNTRY_LANGUAGE_MAP[code];
  if (!languageCode) return undefined;
  return i18n.t('settings:language.defaultLanguage', { language: getLanguageLabel(languageCode) });
}

export function getProfileLanguageOptions(): ProfileLocaleOption[] {
  return APP_LANGUAGE_CODES.map((code) => ({
    value: code,
    label: getLanguageLabel(code),
  }));
}

export function getProfileCountryOptions(): ProfileLocaleOption[] {
  return COUNTRY_CODES.map((code) => ({
    value: code,
    label: getCountryLabel(code),
    description: getCountryDescription(code),
  }));
}

export const PROFILE_LANGUAGE_OPTIONS: ProfileLocaleOption[] = getProfileLanguageOptions();
export const PROFILE_COUNTRY_OPTIONS: ProfileLocaleOption[] = getProfileCountryOptions();

export function normalizeProfileLanguageCode(code?: string | null): string {
  const normalized = normalizeProfileLanguageCodeRaw(code);
  const effective = normalized === 'auto' ? DEFAULT_PROFILE_LANGUAGE_CODE : normalized;
  return toAppLanguageCode(effective);
}

export function normalizeProfileCountryCode(code?: string | null): string {
  const normalized = typeof code === 'string' ? code.trim().toUpperCase() : '';
  if (!normalized) return '';
  return COUNTRY_CODES.includes(normalized) ? normalized : '';
}

export function getDefaultLanguageForCountry(code?: string | null): string | null {
  const normalized = normalizeProfileCountryCode(code);
  if (!normalized) return null;
  const raw = COUNTRY_LANGUAGE_MAP[normalized];
  if (!raw) return null;
  // 국가→언어 매핑은 50여 개 로케일을 포함하지만, 앱 UI가 실제 번역을 제공하는
  // 5개 AppLocale 중 하나로 폴백시켜 드롭다운 옵션과 항상 일치하도록 한다.
  return toAppLanguageCode(raw);
}

export function getCountryCodeFromLanguage(code?: string | null): string {
  const normalized = normalizeProfileLanguageCode(code);
  const parts = normalized.split('-');
  const region = parts[parts.length - 1]?.toUpperCase() ?? '';
  if (/^[A-Z]{2}$/.test(region) && COUNTRY_CODES.includes(region)) {
    return region;
  }
  for (const [countryCode, languageCode] of Object.entries(COUNTRY_LANGUAGE_MAP)) {
    if (languageCode === normalized) return countryCode;
  }
  return '';
}
